import React from "react";

interface ReasonStats {
  topReasons: { reason: string; percent: number; count: number }[];
  dominantReason: string;
  yearOverYearChange: number;
  dominantReasonCount: number;
  mostVolatileReason: string;
  mostVolatileReasonChange: number;
  mostStableReason: string;
  mostStableReasonChange: number;
  priorYear: number;
}

interface Props {
  evictionStats: ReasonStats;
}

const EvictionReasonStatsCard: React.FC<Props> = ({ evictionStats }) => {
  // Color definitions
  const colors = {
    primary: "#f8841c",       // Your specified orange
    textDark: "#3A4A5F",      // Dark text color
    textLight: "#5D6D83",     // Light text color
    positive: "#6A9D7B",      // Green for positive trends
    negative: "#C37D7D",      // Red for negative trends
    dominant: "#89AEB2",      // Teal for dominant reason
    volatile: "#8FA7C7",      // Soft blue for most volatile
    stable: "#BFD3C1"         // Light green for most stable
  };

  const isPositiveTrend = evictionStats.yearOverYearChange >= 0;
  const trendColor = isPositiveTrend ? colors.positive : colors.negative;
  const trendIcon = isPositiveTrend ? "↑" : "↓";

  // Using your specified color palette for the bars
  const colorPalette = [
    "#A7C7E7", "#BFD3C1", "#89AEB2", "#D3DCE3", 
    "#A5BFD6", "#C5D8E1", "#8FA7C7", "#E2E8F0",
    "#9DBAD5", "#B7C8DB", "#98AFC7", "#C0D3E0"
  ];

  return (
    <div style={{
      backgroundColor: "#F8FBFD",
      borderRadius: "10px",
      padding: "16px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      height: "420px",
      display: "flex",
      flexDirection: "column",
      border: "1px solid #D3DCE3",
      fontFamily: "'Segoe UI', 'Helvetica Neue', sans-serif"
    }}>
      {/* Header - updated to match your requested style */}
      <div style={{ marginBottom: "12px" }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          marginBottom: "4px"
        }}>
          <div style={{
            width: '24px',
            height: '24px',
            backgroundColor: colors.primary,
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2">
              <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
            </svg>
          </div>
          <h4 style={{
            color: colors.textDark,
            margin: 0,
            fontSize: '18px',
            fontWeight: 700,
            letterSpacing: '-0.25px'
          }}>
            Eviction Reason Trends
          </h4>
        </div>
        <p style={{
          color: colors.textLight,
          margin: 0,
          fontSize: "12px",
          marginLeft: "34px" // Align with the text
        }}>
          Composition analysis by reasons.
        </p>
      </div>

      {/* Top Reasons */}
      <div style={{ marginBottom: "8px" }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: "10px",
          color: colors.textLight,
          fontSize: "12px",
          fontWeight: 500,
          marginBottom: "6px"
        }}>
          <span>Top Reasons</span>
          <span>% of Total</span>
        </div>
        {evictionStats.topReasons.map((item, index) => (
          <div key={index} style={{ 
            marginBottom: "6px",
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: "10px",
            alignItems: "center"
          }}>
            <span style={{ 
              color: colors.textDark, 
              fontSize: "13px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis"
            }}>
              {item.reason}
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ 
                color: colors.textDark, 
                fontSize: "13px", 
                fontWeight: 600,
                minWidth: "30px",
                textAlign: "right"
              }}>
                {item.percent}%
              </span>
              <div style={{
                width: "40px",
                height: "4px",
                backgroundColor: colorPalette[index % colorPalette.length],
                borderRadius: "2px"
              }} />
            </div>
          </div>
        ))}
      </div>

      {/* Metrics Container */}
<div style={{
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "10px",
  marginBottom: "10px",  // slightly reduced
  flex: 1
}}>
  {/* Dominant Reason */}
  <div style={{
    backgroundColor: "white",
    borderRadius: "6px",
    padding: "10px",  // reduced from 12px
    borderLeft: `3px solid ${colors.dominant}`,
    display: "flex",
    flexDirection: "column"
  }}>
    <div style={{ 
      color: colors.primary,
      fontSize: "11px",
      marginBottom: "6px",  // reduced from 8px
      fontWeight: 600
    }}>
      Dominant Reason
    </div>
    <div style={{ 
      color: colors.textDark,
      fontSize: "12px",
      fontWeight: 700,
      marginBottom: "2px"  // reduced from 4px
    }}>
      {evictionStats.dominantReason}
    </div>
    <div style={{ 
      color: colors.dominant,
      fontSize: "14px",
      fontWeight: 600
    }}>
      {evictionStats.dominantReasonCount.toLocaleString()}
    </div>
  </div>

  {/* Current Trend */}
  <div style={{
    backgroundColor: "white",
    borderRadius: "6px",
    padding: "10px",  // reduced from 12px
    borderLeft: `3px solid ${trendColor}`,
    display: "flex",
    flexDirection: "column"
  }}>
    <div style={{ 
      color: colors.primary,
      fontSize: "12px",
      marginBottom: "6px",  // reduced from 8px
      fontWeight: 600
    }}>
      Current Trend
    </div>
    <div style={{ 
      color: trendColor,
      fontSize: "16px",
      fontWeight: 700,
      marginBottom: "2px",  // reduced from 4px
      display: "flex",
      alignItems: "center",
      gap: "4px"
    }}>
      {trendIcon} {Math.abs(evictionStats.yearOverYearChange)}%
    </div>
    <div style={{ 
      color: colors.textLight,
      fontSize: "12px"
    }}>
      vs. {evictionStats.priorYear}
    </div>
  </div>
</div>


      {/* Stability Container */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "10px",
        marginTop: "auto"
      }}>
        {/* Most Volatile - with original soft blue border */}
        <div style={{
          backgroundColor: "white",
          borderRadius: "6px",
          padding: "10px",
          borderLeft: `3px solid ${colors.volatile}`
        }}>
          <div style={{ 
            color: colors.primary,
            fontSize: "11px",
            marginBottom: "6px",
            fontWeight: 600
          }}>
            Most Volatile
          </div>
          <div style={{ 
            color: colors.textDark,
            fontSize: "13px",
            fontWeight: 600,
            marginBottom: "2px"
          }}>
            {evictionStats.mostVolatileReason}
          </div>
          <div style={{ 
            color: colors.negative,
            fontSize: "12px"
          }}>
            ±{evictionStats.mostVolatileReasonChange}% YoY
          </div>
        </div>

        {/* Most Stable - with original light green border */}
        <div style={{
          backgroundColor: "white",
          borderRadius: "6px",
          padding: "10px",
          borderLeft: `3px solid ${colors.stable}`
        }}>
          <div style={{ 
            color: colors.primary,
            fontSize: "11px",
            marginBottom: "6px",
            fontWeight: 600
          }}>
            Most Stable
          </div>
          <div style={{ 
            color: colors.textDark,
            fontSize: "13px",
            fontWeight: 600,
            marginBottom: "2px"
          }}>
            {evictionStats.mostStableReason}
          </div>
          <div style={{ 
            color: colors.positive,
            fontSize: "12px"
          }}>
            ±{evictionStats.mostStableReasonChange}% YoY
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{
        marginTop: "8px",
        color: colors.textLight,
        fontSize: "10px",
        textAlign: "right"
      }}>
        Updated: {new Date().toLocaleDateString()}
      </div>
    </div>
  );
};

export default EvictionReasonStatsCard;